import { ReactNode, useMemo } from "react";
import styles from "./ProgressIndicator.module.scss";
import { motion } from "framer-motion";

interface CommonProps extends React.HTMLAttributes<HTMLDivElement> {
  label:
    | string
    | ReactNode
    | (({
        total,
        value,
        percentage,
      }: {
        total: number;
        value: number;
        percentage: number;
      }) => string | ReactNode);
  id: string;
  hideLabel?: boolean;
  /**
   * Number representing 100%
   * @note must be larger than value
   * @default 100
   */
  total?: number;
  /**
   * Width in percentage of parent element
   * @default 100
   */
  trackWidth?: number;
  /**
   * Height in pixels
   * @default 20
   */
  trackHeight?: number;
  /**
   * Any valid CSS color value
   * @default var(--wlcm-color-inactive-grey);
   */
  trackColor?: string;
  /**
   * Direction the progress bar expands
   * @default "left"
   */
  origin?: "left" | "right";
}

type SingleVariant = {
  strategy?: never;
  value: number;
  values?: never;
  /**
   * Any valid CSS color value or a function that returns a color value
   * @default "var(--wlcm-color-blue)"
   */
  barColor?: string | ((percentage: number) => string);
  noBoxShadow?: never;
  hideGroupLabels?: never;
  hideRestCount?: never;
  restCountLabel?: never;
};

type MultiVariant = {
  /**
   * Decides whether to stack or overlap the bars
   * @note Use "overlap" if comparing values, and "stack" othwerwise
   * @default "stack"
   */
  strategy?: "overlap" | "stack";
  value?: never;
  values: {
    id: string;
    value: number;
    valueFormatter?: (val: number) => string | number;
    includeInTotal?: boolean;
    /**
     * Any valid CSS color value
     */
    color: string;
    label: string | ReactNode;
  }[];
  barColor?: never;
  /**
   * Whether or not to remove the box shadow from each bar
   * @note The box shadow generally makes it easier to distinguish between bars
   * @default false
   */
  noBoxShadow?: boolean;
  /**
   * Whether or not to hide the labels for each group below the bar
   * @default false
   */
  hideGroupLabels?: boolean;
  /**
   * Whether or not to hide the remaining count outside any groups of the total
   * @default false
   */
  hideRestCount?: boolean;
  restCountLabel?: string;
};

type ProgressIndicatorProps = CommonProps & (SingleVariant | MultiVariant);

export function ProgressIndicator({
  label,
  id,
  hideLabel = false,
  total = 100,
  trackWidth = 100,
  trackHeight = 20,
  trackColor = "var(--wlcm-color-inactive-grey)",
  barColor = "var(--wlcm-color-blue)",
  strategy = "stack",
  origin = "left",
  value,
  values,
  noBoxShadow = false,
  hideGroupLabels = false,
  hideRestCount = false,
  restCountLabel = "remaining",
  ...props
}: ProgressIndicatorProps) {
  const minBarWidth = Math.min(trackHeight, 20); // same as trackHeight, but max 20px
  const sortedValues = useMemo(
    () => values?.sort((a, b) => a.value - b.value) || [],
    [values],
  );
  // If SingleMode, return value, otherwise if strategy "stack" return sum of all values, otherwise 0 as backup
  const [calculatedTotalValue, actualTotalValue] = useMemo(
    () =>
      value !== undefined
        ? [value, value]
        : strategy === "stack"
          ? sortedValues?.reduce(
              (acc, item) => {
                const actualValue = acc[0] + item.value;
                const calculatedValue =
                  acc[1] +
                  (item.includeInTotal === undefined || item.includeInTotal
                    ? item.value
                    : 0);
                return [calculatedValue, actualValue];
              },
              [0, 0],
            ) || [0, 0]
          : [0, 0],
    [value, strategy, sortedValues],
  );
  // If SingleMode, simple calculation, otherwise if strategy "stack" return total - sum of all values, otherwise total - largest value
  const restCount = useMemo(
    () =>
      value !== undefined
        ? total - value
        : strategy === "stack"
          ? total - actualTotalValue
          : total - sortedValues[sortedValues.length - 1]?.value || 0,
    [total, strategy, actualTotalValue, sortedValues],
  );

  const getPercentage = (value = 0, round?: boolean) => {
    let res = (value / total) * 100;
    if (!Number.isFinite(res)) res = 0;
    if (round) {
      return Math.round(res);
    }
    return res;
  };

  return (
    <div {...props} className={`${styles.container} ${props.className}`}>
      <label className={hideLabel ? "sr-only" : ""} id={id}>
        {typeof label === "function"
          ? label({
              total,
              value: calculatedTotalValue,
              percentage: getPercentage(calculatedTotalValue, true),
            })
          : label}
      </label>

      {values ? (
        <>
          <div
            role="img"
            aria-labelledby={id}
            className={styles.track}
            style={{
              width: `${trackWidth}%`,
              height: `${trackHeight}px`,
              backgroundColor: trackColor,
            }}
          >
            {sortedValues.map((item, index) => {
              const previousTotal = sortedValues
                .slice(0, index)
                .reduce((acc, curr) => acc + curr.value, 0);
              return (
                <motion.div
                  key={item.id}
                  initial={{ width: 0 }}
                  animate={
                    strategy === "stack"
                      ? {
                          // Account for shift being applied further down
                          width: `calc(${getPercentage(item.value)}% + ${minBarWidth}px)`,
                        }
                      : {
                          width: `${getPercentage(item.value)}%`,
                        }
                  }
                  transition={{
                    duration: 0.5,
                    delay: (values.length - 1 - index) * 0.2,
                    ease: "easeInOut",
                  }}
                  className={styles.bar}
                  style={{
                    backgroundColor: item.color,
                    zIndex: sortedValues.length - index, // Ensure shortest bar is on top
                    minWidth: item.value > 0 ? minBarWidth : 0, // Ensure bars are visible even if low value
                    ...(origin === "right" ? { left: "auto", right: 0 } : {}),
                    ...(strategy === "stack"
                      ? {
                          // Shift bar to the left to fill gaps
                          [origin]: `calc(${getPercentage(previousTotal)}% - ${minBarWidth}px)`,
                        }
                      : {}),
                    ...(noBoxShadow ? { boxShadow: "none" } : {}),
                  }}
                  aria-label={`${item.value} out of ${total} (${getPercentage(item.value, true)}%) ${item.label}`}
                />
              );
            })}
          </div>
          {!hideGroupLabels && (
            <div className={styles.groupLabels} aria-hidden>
              {sortedValues.map((item) => (
                <div key={item.id} className={styles.label}>
                  <div
                    className={styles.colorDot}
                    style={{ backgroundColor: item.color }}
                  />
                  <p>
                    {item.valueFormatter
                      ? item.valueFormatter(item.value)
                      : item.value}{" "}
                    <span>{item.label}</span>
                  </p>
                </div>
              ))}
              {!hideRestCount && (
                <div className={styles.label}>
                  <div
                    className={styles.colorDot}
                    style={{
                      backgroundColor: trackColor,
                    }}
                  />
                  <p>
                    {restCount} <span>{restCountLabel}</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <progress
          className={styles.track}
          value={value}
          max={total}
          aria-labelledby={id}
          style={
            {
              width: `${trackWidth}%`,
              height: `${trackHeight}px`,
              ...(origin === "right" ? { transform: "scaleX(-1)" } : {}),
              "--track-color": trackColor, // Pass trackColor as a CSS variable
              "--bar-color":
                typeof barColor === "string"
                  ? barColor
                  : barColor(getPercentage(value, true)), // Pass barColor as a CSS variable
              "--bar-min-width": value > 0 ? `${minBarWidth}px` : "0px", // Ensure bar is visible even if low value
            } as React.CSSProperties
          }
        />
      )}
    </div>
  );
}
